import { ApiId, NavigationClient } from "@azure/msal-browser";
import { MsalService } from "@ey/voltron-core";
import config from "@ey/voltron-config";
import { Environment } from "../util/environment";

/**
 * TEMPORARY. Delete once the manifest's AppDomain entry for the authority is deployed.
 *
 * Until then the pane may not navigate to the authority, so the redirect runs in an Office dialog
 * instead. MSAL caches the account to localStorage, shared with the pane, so the pane only has to
 * reload afterwards. Turn off with enableSignInDialog in the environment config.
 */

// Own webpack entry and page, so the dialog never loads the app. See webpack/plugins/html-signin-dialog.js.
const DIALOG_PAGE = "/signindialog.html";

// MSAL sets this before asking us to navigate. We refuse, so it has to be cleared or the next
// MSAL call fails with interaction_in_progress.
const INTERACTION_KEY = "msal.interaction.status";

let dialog: Office.Dialog | undefined;
let open = false;

const closeDialog = (): void => {
  dialog?.close();
  dialog = undefined;
  open = false;
};

function openDialog(): Promise<boolean> {
  window.sessionStorage.removeItem(INTERACTION_KEY);

  if (open) {
    return Promise.resolve(false);
  }
  open = true;

  Office.context.ui.displayDialogAsync(`${window.location.origin}${DIALOG_PAGE}`, { height: 20, width: 30 }, (result) => {
    dialog = result.value;
    open = !!dialog;

    dialog?.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
      const signedIn = "message" in arg && !!arg.message;
      closeDialog();
      if (signedIn) {
        window.location.reload();
      }
    });
    dialog?.addEventHandler(Office.EventType.DialogEventReceived, closeDialog);
  });

  // MSAL ignores this result, so the redirect call resolves and MsalService.ready settles.
  return Promise.resolve(false);
}

/** Task pane side. Must run before the first MsalService is constructed. */
export function installSignInDialog(): void {
  if (!Environment.isSignInDialogEnabled()) {
    return;
  }

  const client = new NavigationClient();
  const navigateExternal = client.navigateExternal.bind(client);

  // loginRedirect delegates to acquireTokenRedirect, so this covers both. Logout is left alone.
  client.navigateExternal = (url, options) =>
    options.apiId === ApiId.acquireTokenRedirect ? openDialog() : navigateExternal(url, options);

  // allowRedirectInIframe only stops MSAL's preflight from throwing on Excel for the web before
  // the navigation client is reached. Safe because navigateExternal never navigates; remove both
  // together.
  config.ad.system = { ...config.ad.system, allowRedirectInIframe: true, navigationClient: client };
}

/** Dialog side. A top-level window, so MSAL is free to redirect. */
export async function runSignInDialog(): Promise<void> {
  await Office.onReady();

  const authService = new MsalService(config.ad);
  await authService.ready.catch((error: unknown) => console.error("[signInDialog] Sign-in failed:", error));

  Office.context.ui.messageParent(authService.getActiveAccount()?.username ?? "");
}
